import { useContext, useEffect, useState } from "react";
import {
  Modal,
  Button,
  Space,
  Dropdown,
  Typography,
  notification,
  Form,
  Input,
  Rate,
} from "antd";
import dayjs from "dayjs";
import { useJoinSession } from "../../hooks/useSession";
import { useSessionAccept, useSessionDelete } from "../../api/Sessions";
import CaregiverSchedule from "../CaregiverDisplay/CaregiverSchedule";
import { AuthContext } from "../../useAuth";
import { isCaregiver } from "../../utils/userUtil";
import { useNavigate } from "react-router-dom";
import {
  useEventRecording,
  useEventRecordingRequest,
} from "../../api/Recordings";
import ReactPlayer from "react-player";
import UserRatingForm from "../UserRatingForm/UserRatingForm";

// TODO: Display actions based on user role
const SessionActions = ({ session, guestUser }) => {
  const { user } = useContext(AuthContext);
  const caregiver = isCaregiver(user);

  const sessionDate = session?.StartTime;
  const now = dayjs();
  // const sessionStart = dayjs(sessionDate);
  const sessionEnd = dayjs(session?.EndTime);

  const isPast = now.isAfter(sessionEnd);

  return session?.Status === "canceled" ? (
    <Typography.Paragraph
      type="secondary"
      style={{ width: "150px", textAlign: "center", margin: "0" }}
    >
      Canceled
    </Typography.Paragraph>
  ) : (
    <>
      <Space direction="vertical" style={{ width: "150px" }}>
        {!session ? null : isPast ? (
          caregiver ? (
            <CaregiverPastActions />
          ) : (
            <UserPastActions
              sessionId={session?.SessionId}
              guestUser={guestUser}
            />
          )
        ) : caregiver ? (
          <CaregiverFutureActions session={session} guestUser={guestUser} />
        ) : (
          <UserFutureActions session={session} guestUser={guestUser} />
        )}
      </Space>
    </>
  );
};

const CaregiverFutureActions = ({ session, guestUser }) => {
  const [disabled, setDisabled] = useState(false);
  const { user } = useContext(AuthContext);

  const { Status } = session;

  const [showCancel, setShowCancel] = useState(false);

  const sessionDelete = useSessionDelete(session?.SessionId);
  const sessionAccept = useSessionAccept(user?.UserId, session?.SessionId);

  const cancelSession = async () => {
    setDisabled(true);
    sessionDelete
      .mutateAsync()
      .then((r) => {
        setShowCancel(false);
        notification.success({
          message: "Session Canceled",
          description: "OK",
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const acceptSession = async () => {
    setDisabled(true);
    sessionAccept
      .mutateAsync()
      .then(() =>
        notification.success({
          message: "Session Accepted!",
          description: "",
        })
      )
      .finally(() => {
        setDisabled(false);
      });
  };

  const { canJoin, displayText } = useJoinSession(session);

  const navigate = useNavigate();

  const joinSession = () => {
    navigate(`/join/sessions/${session?.SessionId}`);
  };

  return Status === "pending" ? (
    <>
      <Button
        type="primary"
        onClick={() => acceptSession()}
        block
        disabled={disabled}
      >
        Accept
      </Button>
      <Button
        danger
        type="text"
        onClick={() => setShowCancel(true)}
        block
        disabled={disabled}
      >
        Decline
      </Button>

      <Modal
        open={showCancel}
        title="Decline Session?"
        onCancel={() => setShowCancel(false)}
        footer={[
          <Button key="close" type="text" onClick={() => setShowCancel(false)}>
            Nevermind
          </Button>,
          <Button
            danger
            key="cancel"
            type="primary"
            onClick={() => cancelSession()}
          >
            Yes, cancel
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          Are you sure you wish to decline your{" "}
          <Typography.Text strong>{session?.Category} Session</Typography.Text>{" "}
          with {guestUser?.FirstName} on{" "}
          {dayjs(session?.StartTime).format("MMM Do @ h:mma")}?
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          You can decline up to 30 minutes prior to the start of a session.
        </Typography.Paragraph>
      </Modal>
    </>
  ) : (
    <>
      <JoinSessionButton
        disabled={!canJoin}
        onClick={() => joinSession()}
        displayText={displayText}
      />
    </>
  );
};

// TODO: Validate session time for modify actions
const UserFutureActions = ({ session, guestUser }) => {
  const [showCancel, setShowCancel] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const { canJoin, displayText } = useJoinSession(session);

  const sessionDelete = useSessionDelete(session?.SessionId);
  const cancelSession = async () => {
    sessionDelete.mutateAsync().then((r) => {
      setShowCancel(false);
      notification.success({ message: "Session Canceled", description: "OK" });
    });
  };

  const items = [
    { label: "Reschedule", key: "reschedule" },
    { label: "Cancel", key: "cancel" },
  ];

  const menuClick = (e) => {
    switch (e.key) {
      case "cancel":
        setShowCancel(true);
        break;
      case "reschedule":
        setShowReschedule(true);
        break;
    }
  };

  const navigate = useNavigate();

  const joinSession = () => {
    navigate(`/join/sessions/${session?.SessionId}`);
  };

  return (
    <>
      <JoinSessionButton
        disabled={!canJoin}
        onClick={() => joinSession()}
        displayText={displayText}
      />
      <Dropdown
        menu={{
          items,
          onClick: menuClick,
        }}
        placement="bottom"
      >
        <Button block type="text">
          Modify
        </Button>
      </Dropdown>

      <Modal
        open={showReschedule}
        title="Reschedule Session"
        onCancel={() => {
          setShowReschedule(false);
        }}
      >
        <CaregiverSchedule
          caregiver={guestUser}
          onClose={() => setShowReschedule(false)}
        />
      </Modal>

      <Modal
        open={showCancel}
        title="Cancel Session?"
        onCancel={() => setShowCancel(false)}
        footer={[
          <Button key="close" type="text" onClick={() => setShowCancel(false)}>
            Nevermind
          </Button>,
          <Button
            danger
            key="cancel"
            type="primary"
            onClick={() => cancelSession()}
          >
            Yes, cancel
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          Are you sure you wish to cancel your{" "}
          <Typography.Text strong>{session?.Category} Session</Typography.Text>{" "}
          with {guestUser?.FirstName} on{" "}
          {dayjs(session?.StartTime).format("MMM Do @ h:mma")}?
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          You can cancel up to 30 minutes prior to the start of a session. You
          will not be charged for canceling.
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

// TODO: Add countdown timer
const JoinSessionButton = ({ displayText = "Join", disabled, onClick }) => {
  return (
    <Button block type="primary" disabled={disabled} onClick={() => onClick()}>
      {displayText}
    </Button>
  );
};

// TODO: Caregiver review of user
const CaregiverPastActions = () => {
  return null;
};

// TODO: User review of caregiver & request recording
const UserPastActions = ({ sessionId, guestUser }) => {
  const [showReview, setShowReview] = useState(false);
  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }}>
        <RecordingButton eventId={sessionId} />
        <Button block onClick={() => setShowReview(true)}>
          Review
        </Button>
      </Space>
      <Modal
        title={<>Review {guestUser?.FirstName}</>}
        open={showReview}
        footer={null}
        onCancel={() => setShowReview(false)}
        destroyOnClose
      >
        <UserRatingForm
          onFinish={() => setShowReview(false)}
          userId={guestUser?.UserId}
        />
      </Modal>
    </>
  );
};

const RecordingButton = ({ eventId }) => {
  const [disabled, setDisabled] = useState(false);
  const [recording, setRecording] = useState();
  const [recordingUrl, setRecordingUrl] = useState();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const { data, isLoading, refetch } = useEventRecording(eventId);
  const eventRedordingRequest = useEventRecordingRequest(eventId);

  useEffect(() => {
    setRecording(data?.Recording);
    setRecordingUrl(data?.URL);
  }, [data]);

  const getStatusText = () => {
    if (!recording) return "Request";
    else
      switch (recording?.RecordingState) {
        case "completed":
          return "Play Recording";
        case "deleted":
          return "Recording deleted";
        default:
          return "Processing";
      }
  };

  const handleClick = async () => {
    if (!recording) eventRedordingRequest.mutateAsync({});
    else
      switch (recording?.RecordingState) {
        case "completed":
          setShowVideoModal(true);
          return;
        case "deleted":
          return;
        default:
          return;
      }
  };

  useEffect(() => {
    const interval = 30 * 1000; // 30 seconds
    let timer;

    if (recording?.RecordingState !== "completed" && recording) {
      timer = setInterval(() => {
        refetch();
      }, interval);
    } else {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [recording?.data?.Recording]);

  return (
    <>
      <Button
        block
        type="primary"
        disabled={disabled || isLoading}
        loading={isLoading}
        onClick={() => handleClick()}
      >
        {getStatusText()}
      </Button>
      <Modal
        // title="Recording player"
        centered
        open={showVideoModal}
        footer={null}
        onCancel={() => setShowVideoModal(false)}
        destroyOnClose
      >
        <ReactPlayer
          width="100%"
          height="100%"
          controls={true}
          url={recordingUrl}
        />
      </Modal>
    </>
  );
};

export default SessionActions;
